import * as React from "react";
import { cn } from "@/lib/utils";
import { UseFormRegisterReturn } from "react-hook-form";
import { cva, type VariantProps } from "class-variance-authority";

// Defines style variants for the textarea component
const textAreaVariants = cva(
  cn(
    "border-gray-500 placeholder:text-gray-normal text-gray-stronger flex min-w-0 border bg-transparent outline-none",
    "hover:border-primary-800",
    "focus:border-primary-800 active:border-primary-800 active:shadow-input-active",
    "disabled:pointer-events-none disabled:cursor-not-allowed disabled:bg-input-disable disabled:border-gray-200 disabled:text-gray-light",
    "file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground ",
  ),
  {
    variants: {
      size: {
        lg: "h-px-44 rounded-input-lg px-[14px] py-[10px]",
        md: "h-px-40 rounded-input-md px-[12px] py-[8px]",
        sm: "h-px-32 rounded-input-sm px-[10px] py-[6px]",
      },
    },
    defaultVariants: {
      size: "md",
    },
  },
);

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLTextAreaElement>, "size">,
    VariantProps<typeof textAreaVariants> {
  label?: string;
  error?: string;
  register?: UseFormRegisterReturn;
}

// Renders a styled textarea with optional label, validation, and form registration support
const TextArea = React.forwardRef<HTMLTextAreaElement, InputProps>(
  ({ className, label, error, register, size, ...props }, ref) => {
    return (
      <div className="flex w-[inherit] flex-col gap-1">
        {label && (
          <label className="text-sm font-medium text-gray-800">
            {label}{" "}
            {register?.required && <span className="text-red-700">*</span>}
          </label>
        )}
        <textarea
          className={cn(
            textAreaVariants({ size, className }),
            error && "border-red-700 focus:border-red-700 focus:ring-red-700",
            className,
          )}
          ref={ref}
          {...register}
          {...props}
        />
        {error && <p className="text-xs text-red-700">{error}</p>}
      </div>
    );
  },
);
TextArea.displayName = "TextArea";
export { TextArea };
